import React, { useCallback, useEffect, useMemo } from 'react';
import { JSONSchema7 } from 'json-schema';
import { useDebouncedCallback } from 'use-debounce';
import { AppSchemaForm, RawJsonEditor, SchemaFormData, useRawJsonDraft } from '../../../../components/SchemaForm';
import { QEFormEntryMode } from '../../components/QuoteExperiencePanel/QuoteExperiencePanel.component';
import { QuoteExperienceState } from '../../QuoteExperienceQuickSetup.page';

const RAW_JSON_DEBOUNCE_MS = 300;

export interface QESchemaPanelProps {
    schema: JSONSchema7;
    schemaType: string;
    current: Partial<QuoteExperienceState>;
    extraErrorsByPointer?: Record<string, string[]>;
    imagePreviewMap?: Record<string, string>;
    updateData: (pointer: string, value: any, setInitial?: boolean) => void;
    formMode: QEFormEntryMode;
    onFormModeToggle?: (formMode: QEFormEntryMode) => void;
}

export const QESchemaPanel: React.FC<QESchemaPanelProps> = ({
    schema,
    schemaType,
    current,
    extraErrorsByPointer,
    imagePreviewMap,
    updateData,
    formMode,
    onFormModeToggle
}) => {
    const formValues = useMemo(() =>
        current.formDataByType?.[schemaType] || {}
        , [current, schemaType]);

    const handleUpdateData = useCallback((data: SchemaFormData) => {
        updateData(`/formDataByType/${schemaType}`, data);
    }, [schemaType, updateData]);

    const draft = useRawJsonDraft(formValues, handleUpdateData, formMode === 'json');
    const { error: draftError, commit, setText } = draft;

    const debouncedCommit = useDebouncedCallback((text: string) => { commit(text, { silent: true }); }, RAW_JSON_DEBOUNCE_MS);

    const handleTextChange = useCallback((text: string) => {
        setText(text);
        debouncedCommit(text);
    }, [setText, debouncedCommit]);

    useEffect(() => {
        if (formMode === 'form' && draftError && onFormModeToggle) {
            onFormModeToggle('json');
        }
    }, [formMode, draftError, onFormModeToggle]);

    const handleBlur = () => {
        debouncedCommit.cancel();
        if (!commit() && onFormModeToggle) {
            onFormModeToggle('json');
        }
    };

    return (
        <div className='app-qe-panel-content' >
            {
                formMode === 'form'
                    ? (
                        <AppSchemaForm
                            schema={schema}
                            values={formValues}
                            onChange={handleUpdateData}
                            extraErrorsByPointer={extraErrorsByPointer}
                            imagePreviewMap={imagePreviewMap}
                        />
                    )
                    : (
                        <RawJsonEditor
                            text={draft.text}
                            error={draft.error}
                            onTextChange={handleTextChange}
                            onBlur={handleBlur}
                        />
                    )
            }
        </div>
    );
};
